import React, { useState, useEffect } from "react";
import "./AddEquipment.css"; // Add styles for the layout
import "./Equipment.css"; // Equipment specific styles
import "./EquipmentList.css"; // Equipment list styles
import "../../shared/styles/snackbar.css";
import backIcon from "../../shared/assets/icons/back.png";
import uploadPlaceholder from "../../shared/assets/icons/upload.png";
import profileIcon from '../../shared/assets/icons/profile.png';
import bellIcon from '../../shared/assets/icons/bell.png';
import micIcon from '../../shared/assets/icons/mic.png';
import { useNavigate } from 'react-router-dom';
import SearchableDropdown from '../../components/SearchableDropdown';
import { equipmentService } from './services/equipmentService';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { getFirstErrorMessage } from '../../shared/utils/validator';
import HeaderNav from "../../shared/components/HeaderNav";
import { IoArrowUndoOutline } from "react-icons/io5";
import MobileFooterNav from "../../shared/components/MobileFooterNav";

const Equipment = () => {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [showNotes, setShowNotes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [products, setProducts] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [addresses, setAddresses] = useState([]);

  const [productPage, setProductPage] = useState(1);
  const [productHasMore, setProductHasMore] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const productSearchRef = React.useRef('');

  const [makePage, setMakePage] = useState(1);
  const [makeHasMore, setMakeHasMore] = useState(true);
  const [isLoadingMakes, setIsLoadingMakes] = useState(false);
  const makeSearchRef = React.useRef('');

  const [modelPage, setModelPage] = useState(1);
  const [modelHasMore, setModelHasMore] = useState(true);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const modelSearchRef = React.useRef('');
  const CONTRACT_TYPES = [
    { label: 'Paid', value: 0 },
    { label: 'Under Warranty', value: 1 },
    { label: 'AMC', value: 3 },
  ];

  const [formData, setFormData] = useState({
    name: '',
    product: '',
    make: '',
    model: '',
    serialNumber: '',
    location: '',
    details: '',
    contractType: '',
    warrantyValidUpto: '',
    amcFrom: '',
    amcTo: ''
  });
  const [searchTerms, setSearchTerms] = useState({
    product: '',
    make: '',
    model: ''
  });

  const handleToggleNotes = () => {
    setShowNotes(!showNotes);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProductChange = async (productId) => {
    const selectedProduct = products.find(p => p.id == productId);
    setFormData(prev => ({ ...prev, product: productId, make: '', model: '' }));
    setSearchTerms(prev => ({ ...prev, product: selectedProduct?.name || '', make: '', model: '' }));
    setModels([]);
    setMakePage(1); setMakeHasMore(true); makeSearchRef.current = '';
    if (productId) await loadMakes(productId, '', 1);
    else setMakes([]);
  };

  const handleMakeChange = async (makeId) => {
    const selectedMake = makes.find(m => m.id == makeId);
    setFormData(prev => ({ ...prev, make: makeId, model: '' }));
    setSearchTerms(prev => ({ ...prev, make: selectedMake?.make_name || '', model: '' }));
    setModelPage(1); setModelHasMore(true); modelSearchRef.current = '';
    if (makeId && formData.product) await loadModels(makeId, formData.product, '', 1);
    else setModels([]);
  };

  const handleProductClear = () => {
    setFormData(prev => ({ ...prev, product: '', make: '', model: '' }));
    setSearchTerms(prev => ({ ...prev, product: '', make: '', model: '' }));
    setMakes([]); setModels([]);
    setProductPage(1); setProductHasMore(true); productSearchRef.current = '';
    setMakePage(1); setMakeHasMore(true);
    setModelPage(1); setModelHasMore(true);
  };

  const handleMakeClear = () => {
    setFormData(prev => ({ ...prev, make: '', model: '' }));
    setSearchTerms(prev => ({ ...prev, make: '', model: '' }));
    setModels([]);
    setMakePage(1); setMakeHasMore(true); makeSearchRef.current = '';
    setModelPage(1); setModelHasMore(true);
  };

  const validateForm = () => {
    const requiredFields = ['name', 'product', 'make', 'model'];
    for (const field of requiredFields) {
      if (!formData[field] || !formData[field].toString().trim()) {
        showSnackbar('Please fill in all the required fields', 'error');
        return false;
      }
    }
    if (formData.contractType === '') {
      showSnackbar('Please select a contract type', 'error');
      return false;
    }
    if (formData.contractType === 1 && !formData.warrantyValidUpto) {
      showSnackbar('Please select warranty validity date', 'error');
      return false;
    }
    if (formData.contractType === 3 && (!formData.amcFrom || !formData.amcTo)) {
      showSnackbar('Please select AMC from and to dates', 'error');
      return false;
    }
    return true;
  };

  const navigate = useNavigate();

  const navigateToTaskList = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const toDateTime = (date) => date ? `${date} 00:00:00.000` : undefined;

      const equipmentData = {
        name: formData.name,
        serial_identifier: formData.name,
        category_id: formData.product,
        make_id: formData.make,
        model_id: formData.model,
        serial_number: formData.serialNumber,
        contract_type: formData.contractType,
        ...(formData.contractType === 1 && { valid_upto: toDateTime(formData.warrantyValidUpto) }),
        ...(formData.contractType === 3 && {
          'service_contract[period_start]': toDateTime(formData.amcFrom),
          'service_contract[period_end]': toDateTime(formData.amcTo),
        }),
      };
      
      const response = await equipmentService.createEquipment(equipmentData);
      
      if (response.success) {
        navigate('/equipmentlist');
      } else {
        const errorMessage = response?.errors ? getFirstErrorMessage(response) : ('An error occurred. Please try again.');
        showSnackbar(errorMessage, 'error');
      }
    } catch (error) {
      
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClientDetails = async () => {
    try {
      const data = await equipmentService.fetchClientDetails();
      setAddresses(data.address || []);
    } catch (error) {
      console.error('Error fetching client details:', error);
    }
  };

  const loadProducts = async (searchQuery = '', page = 1) => {
    if (isLoadingProducts) return;
    setIsLoadingProducts(true);
    try {
      const response = await equipmentService.searchProducts(searchQuery, page);
      if (response.success && Array.isArray(response.data)) {
        const formatted = response.data.map(item => ({ id: item.id || item.product_id, name: item.name || item.product_name }));
        setProducts(prev => page === 1 ? formatted : [...prev, ...formatted]);
        setProductHasMore(response.data.length === 10);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const loadMakes = async (productId, searchQuery = '', page = 1) => {
    if (isLoadingMakes) return;
    setIsLoadingMakes(true);
    try {
      const response = await equipmentService.searchMakes(searchQuery, productId, page);
      if (response.success && Array.isArray(response.data)) {
        const formatted = response.data.map(item => ({ id: item.id || item.make_id, make_name: item.name || item.make_name }));
        setMakes(prev => page === 1 ? formatted : [...prev, ...formatted]);
        setMakeHasMore(response.data.length === 10);
      }
    } catch (error) {
      console.error('Error loading makes:', error);
    } finally {
      setIsLoadingMakes(false);
    }
  };

  const loadModels = async (makeId, productId, searchQuery = '', page = 1) => {
    if (isLoadingModels) return;
    setIsLoadingModels(true);
    try {
      const response = await equipmentService.searchModels(searchQuery, makeId, productId, page);
      if (response.success && Array.isArray(response.data)) {
        const formatted = response.data.map(item => ({ id: item.id || item.model_id, model_name: item.name || item.model_name }));
        setModels(prev => page === 1 ? formatted : [...prev, ...formatted]);
        setModelHasMore(response.data.length === 10);
      }
    } catch (error) {
      console.error('Error loading models:', error);
    } finally {
      setIsLoadingModels(false);
    }
  };

  useEffect(() => {
    loadProducts('', 1);
    fetchClientDetails();
  }, []);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setUploadedImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="maintenance-request">
   <HeaderNav profileIcon={profileIcon} />
      {/* Header Section */}
      
      <div className="second-header">
        <button className="back-button" >
           <IoArrowUndoOutline color="black" size={25} alt="Back" onClick={() => navigate('/equipmentlist')}/>
        </button>
        <h2 className="page-title">Add Equipment</h2>
      </div>
     {/* Mobile Header */}
         <header className="mobile-header">
           <div className="header-left">
              <IoArrowUndoOutline color="black" size={25} alt="Back" onClick={() => navigate('/dashboard')} />
           </div>
           <h1 className="app-title">Add Equipment</h1>
         </header>
      {/* Form Section */}
      <div className="form-container">
        <form className="maintenance-form" onSubmit={(e) => { e.preventDefault(); navigateToTaskList(); }}>
        <div className="form-group">
            <textarea
              id="name"
              // className="input-field name-textarea"
                 containerClassName="dropdown-container"
              className="dropdown-input"
              rows="1"
              placeholder="Name *"
              value={formData.name}
              maxLength={40}
              onChange={(e) => handleInputChange('name', e.target.value)}
            ></textarea>
          </div>
          <div className="form-group">
            <SearchableDropdown
              options={products}
              value={searchTerms.product}
              onChange={(term) => {
                setSearchTerms(prev => ({ ...prev, product: term }));
                productSearchRef.current = term;
                setProductPage(1);
                setProductHasMore(true);
                loadProducts(term, 1);
              }}
              onSelect={(product) => handleProductChange(product.id)}
              onClear={handleProductClear}
              onFocus={() => { if (products.length === 0) loadProducts('', 1); }}
              onScrollEnd={() => {
                if (!isLoadingProducts && productHasMore) {
                  const next = productPage + 1;
                  setProductPage(next);
                  loadProducts(productSearchRef.current, next);
                }
              }}
              loading={isLoadingProducts}
              placeholder="Product *"
              containerClassName="dropdown-container"
              className="dropdown-input"
            />
          </div>
          <div className="form-group">
            <SearchableDropdown
              options={makes.map(make => ({ ...make, name: make.make_name }))}
              value={searchTerms.make}
              onChange={(term) => {
                setSearchTerms(prev => ({ ...prev, make: term }));
                makeSearchRef.current = term;
                setMakePage(1);
                setMakeHasMore(true);
                if (formData.product) loadMakes(formData.product, term, 1);
              }}
              onSelect={(make) => handleMakeChange(make.id)}
              onClear={handleMakeClear}
              onFocus={() => { if (makes.length === 0 && formData.product) loadMakes(formData.product, '', 1); }}
              onScrollEnd={() => {
                if (!isLoadingMakes && makeHasMore && formData.product) {
                  const next = makePage + 1;
                  setMakePage(next);
                  loadMakes(formData.product, makeSearchRef.current, next);
                }
              }}
              loading={isLoadingMakes}
              placeholder="Make *"
              disabled={!formData.product}
              containerClassName="dropdown-container"
              className="dropdown-input"
            />
          </div>
          <div className="form-group">
            <SearchableDropdown
              options={models.map(model => ({ ...model, name: model.model_name }))}
              value={searchTerms.model}
              onChange={(term) => {
                setSearchTerms(prev => ({ ...prev, model: term }));
                modelSearchRef.current = term;
                setModelPage(1);
                setModelHasMore(true);
                if (formData.make && formData.product) loadModels(formData.make, formData.product, term, 1);
              }}
              onSelect={(model) => {
                const selectedModel = models.find(m => m.id == model.id);
                setFormData(prev => ({ ...prev, model: model.id }));
                setSearchTerms(prev => ({ ...prev, model: selectedModel?.model_name || '' }));
              }}
              onClear={() => {
                setFormData(prev => ({ ...prev, model: '' }));
                setSearchTerms(prev => ({ ...prev, model: '' }));
                setModelPage(1); setModelHasMore(true); modelSearchRef.current = '';
              }}
              onFocus={() => { if (models.length === 0 && formData.make && formData.product) loadModels(formData.make, formData.product, '', 1); }}
              onScrollEnd={() => {
                if (!isLoadingModels && modelHasMore && formData.make && formData.product) {
                  const next = modelPage + 1;
                  setModelPage(next);
                  loadModels(formData.make, formData.product, modelSearchRef.current, next);
                }
              }}
              loading={isLoadingModels}
              placeholder="Model *"
              disabled={!formData.make || !formData.product}
              containerClassName="dropdown-container"
              className="dropdown-input"
            />
          </div>
          <div className="form-group">
            <input
              id="serial-number"
              containerClassName="dropdown-container"
              className="dropdown-input"
              rows="1"
              placeholder="Serial Number"
              value={formData.serialNumber}
              onChange={(e) => handleInputChange('serialNumber', e.target.value)}
            />
          </div>
          <div className="form-group">
            <select
              className="dropdown-input"
              value={formData.contractType}
              onChange={(e) => handleInputChange('contractType', e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">Contract Type *</option>
              {CONTRACT_TYPES.map(ct => (
                <option key={ct.value} value={ct.value}>{ct.label}</option>
              ))}
            </select>
          </div>
          {formData.contractType === 1 && (
            <div className="form-group">
              <label className="date-label">Warranty Valid Upto *</label>
              <input
                type="date"
                className="dropdown-input"
                value={formData.warrantyValidUpto}
                onChange={(e) => handleInputChange('warrantyValidUpto', e.target.value)}
              />
            </div>
          )}
          {formData.contractType === 3 && (
            <>
              <div className="form-group">
                <label className="date-label">AMC From *</label>
                <input
                  type="date"
                  className="dropdown-input"
                  value={formData.amcFrom}
                  onChange={(e) => handleInputChange('amcFrom', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="date-label">AMC To *</label>
                <input
                  type="date"
                  className="dropdown-input"
                  value={formData.amcTo}
                  onChange={(e) => handleInputChange('amcTo', e.target.value)}
                />
              </div>
            </>
          )}

          {/* Image Upload Section */}
          {/* <div className="upload-section">
            <label className="upload-container">
              <div className="upload-placeholder">
                {uploadedImage ? (
                  <img
                    src={uploadedImage}
                    alt="Uploaded"
                    className="uploaded-image"
                  />
                ) : (
                  <>
                    <img
                      src={uploadPlaceholder}
                      alt="Upload"
                      className="upload-icon"
                    />
                    <p>Upload Picture</p>
                    <p>Upload or drop your file here</p>
                  </>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="file-input"
              />
            </label>
          </div> */}

          {/* Action Buttons */}
          {isLoading ?
          (  <div className="loading-container">
            <div className="circular-progress"></div>
          </div>):
          (
            <div className="form-actions">
            <button type="button" className="cancel-button" onClick={() => navigate('/equipmentlist')}>
              Cancel
            </button>
         
              <button type="submit" className="save-button">
                Save
              </button>
        
          </div>
          )
          }
        </form>
      </div>
              <MobileFooterNav />
    </div>
  );
};

export default Equipment;